import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { Flex, Spin, Typography, theme } from 'antd';
import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useCasExchangeMutation } from '@/api/auth/mutations';
import { useLoginFlow } from '@/hooks/useLoginFlow';
import { TenantSelector } from '@/components/tenant/TenantSelector';
import { getSafeRedirectPath } from '@/utils/redirect';

const { Text } = Typography;

type CasCompleteSearch = {
  code?: string;
  returnPath?: string;
};

/**
 * Landing page after CAS login. ai-auth redirects here with a one-time `code`, which is exchanged
 * for the regular access token; the rest is the same post-login flow as the login form.
 * Must not live under /auth/*, which the BFF proxies to the gateway.
 */
export const Route = createFileRoute('/sso/cas/complete')({
  component: RouteComponent,
  validateSearch: (search: Record<string, unknown>): CasCompleteSearch => ({
    code: typeof search.code === 'string' ? search.code : undefined,
    returnPath: typeof search.returnPath === 'string' ? search.returnPath : undefined
  })
});

/**
 * Maps the CAS return path to a post-login redirect; `/` means "no preference" so the role-based default applies.
 * @param returnPath - Return path echoed back by ai-auth.
 */
function toCustomRedirect(returnPath?: string): string | undefined {
  const safePath = getSafeRedirectPath(returnPath);
  return safePath && safePath !== '/' ? safePath : undefined;
}

function RouteComponent() {
  const { token } = theme.useToken();
  const { t } = useTranslation(['auth']);
  const navigate = useNavigate();
  const { code, returnPath } = Route.useSearch();
  const casExchange = useCasExchangeMutation();
  const { handlePostLogin, showTenantSelector, handleTenantSelectorClose, handleTenantSelectorConfirm } = useLoginFlow();
  // The code is single-use, so guard against React re-running the effect (StrictMode, remounts)
  const exchangeStarted = useRef(false);

  useEffect(() => {
    if (exchangeStarted.current) {
      return;
    }
    exchangeStarted.current = true;

    if (!code) {
      navigate({ to: '/login', search: { casError: true }, replace: true });
      return;
    }

    const run = async () => {
      try {
        const response = await casExchange.mutateAsync(code);
        await handlePostLogin(response, {
          fetchMemberships: true,
          showTenantSelection: true,
          customRedirect: toCustomRedirect(returnPath)
        });
      } catch {
        navigate({ to: '/login', search: { casError: true }, replace: true });
      }
    };
    void run();
  }, [code, returnPath, casExchange, handlePostLogin, navigate]);

  return (
    <Flex vertical align="center" justify="center" gap={16} style={{ minHeight: '100vh', background: token.colorBgLayout }}>
      <Spin size="large" />
      <Text type="secondary">{t('auth:login.casCompleting')}</Text>
      <TenantSelector open={showTenantSelector} onClose={handleTenantSelectorClose} onTenantSelected={handleTenantSelectorConfirm} />
    </Flex>
  );
}
